import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../api";
import { formatDateTime } from "../../../shared/time";
import type { CalendarItem } from "../../../shared/types";
import { Badge, Dialog, Empty, ErrorNote } from "../../ui";
import { useSession } from "../../shell/session";
import { CapacityMeter, PageFrame, hasRole, messageOf, slugify } from "../calendar/chrome";
import { addDays, rangeFor, todayKey } from "../calendar/dates";
import type { RegistrationRecord } from "./types";

const SLUG = /^[a-z0-9-]{3,60}$/;

export function RegistrationsPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestedEvent = params.get("eventId") ?? "";
  const { user } = useSession();
  const canEdit = hasRole(user?.role, "coordinator");
  const [rows, setRows] = useState<RegistrationRecord[]>([]);
  const [events, setEvents] = useState<CalendarItem[]>([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(canEdit && requestedEvent !== "");
  const [eventId, setEventId] = useState(requestedEvent);
  const [capacity, setCapacity] = useState("20");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [published, setPublished] = useState(true);
  const [allowWaitlist, setAllowWaitlist] = useState(true);
  const [note, setNote] = useState("You are on the list. No email is sent — mail delivery is not configured.");
  const [saving, setSaving] = useState(false);

  function load() {
    void api<RegistrationRecord[]>("/api/v1/registrations").then(setRows).catch((caught: unknown) => setError(messageOf(caught)));
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const bounds = rangeFor(addDays(todayKey(), -14), addDays(todayKey(), 180));
    void api<CalendarItem[]>(`/api/v1/calendar?from=${encodeURIComponent(bounds.from)}&to=${encodeURIComponent(bounds.to)}`)
      .then((items) => setEvents(items.filter((item) => item.kind !== "plan").slice().sort((a, b) => a.startsAt.localeCompare(b.startsAt))))
      .catch(() => setEvents([]));
  }, []);

  const used = new Set(rows.map((row) => row.eventId));

  async function create(event: FormEvent) {
    event.preventDefault();
    setError("");
    const seats = Number(capacity);
    if (!eventId) {
      setError("Choose an event. Schedule one on the calendar first if it is missing.");
      return;
    }
    if (!Number.isInteger(seats) || seats < 1 || seats > 10000) {
      setError("Capacity is a whole number from 1 to 10000.");
      return;
    }
    if (!SLUG.test(slug)) {
      setError("The public link needs 3–60 characters: lowercase letters, numbers, and hyphens.");
      return;
    }
    setSaving(true);
    try {
      const created = await api<RegistrationRecord>("/api/v1/registrations", {
        method: "POST",
        body: JSON.stringify({ eventId, capacity: seats, publicSlug: slug, published, allowWaitlist, confirmationNote: note }),
      });
      navigate(`/registrations/${created.id}`);
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageFrame
      kicker="Operations"
      title="Registrations"
      lede="Published signup pages keep a capacity count. Cancelling a confirmed signup gives the places back."
      actions={canEdit ? <button className="btn primary" type="button" onClick={() => setOpen(true)}>Open a signup</button> : <p className="notice">Opening a signup needs a coordinator login.</p>}
    >
      {error && !open && <ErrorNote>{error}</ErrorNote>}
      {rows.length === 0 ? <Empty title="No signups yet" body="Attach a signup to an event, then publish the public page." /> : (
        <div className="stack">
          {rows.map((row) => (
            <article key={row.id} className="panel panel-pad stack">
              <div className="split">
                <Link to={`/registrations/${row.id}`}><strong>{row.title}</strong></Link>
                <span className="cluster">
                  <Badge tone={row.published ? "teal" : "amber"}>{row.published ? "Published" : "Unpublished"}</Badge>
                  {row.allowWaitlist ? <Badge tone="violet">Waitlist</Badge> : null}
                </span>
              </div>
              <span className="muted">{formatDateTime(row.startsAt)} · {row.place || "Place not set"}</span>
              <CapacityMeter confirmed={row.confirmedCount} remaining={row.remaining} capacity={row.capacity} />
            </article>
          ))}
        </div>
      )}
      {open && (
        <Dialog title="Open a signup" onClose={() => setOpen(false)}>
          <form className="form-grid" onSubmit={(event) => void create(event)}>
            {error && <ErrorNote>{error}</ErrorNote>}
            <div className="field">
              <label htmlFor="reg-event">Event</label>
              <select id="reg-event" value={eventId} onChange={(event) => {
                const next = event.target.value;
                setEventId(next);
                const match = events.find((item) => item.id === next);
                if (match && !slugTouched) setSlug(slugify(match.title));
              }}>
                <option value="">Choose an event</option>
                {events.map((item) => (
                  <option key={item.id} value={item.id} disabled={used.has(item.id)}>
                    {formatDateTime(item.startsAt)} — {item.title}{used.has(item.id) ? " · signup exists" : ""}
                  </option>
                ))}
              </select>
              <p className="faint">Each event can have one signup. Capacity, the link, and the waitlist choice are fixed after you create it.</p>
            </div>
            <div className="field">
              <label htmlFor="reg-capacity">Capacity</label>
              <input id="reg-capacity" inputMode="numeric" value={capacity} onChange={(event) => setCapacity(event.target.value)} required />
            </div>
            <div className="field">
              <label htmlFor="reg-slug">Public link</label>
              <input id="reg-slug" value={slug} onChange={(event) => { setSlugTouched(true); setSlug(event.target.value.trim().toLowerCase()); }} placeholder="long-table-dinner" required />
              <p className="faint">The page will live at /r/{slug || "your-link"}</p>
            </div>
            <label className="cluster"><input type="checkbox" checked={published} onChange={(event) => setPublished(event.target.checked)} /> Publish the public page now</label>
            <label className="cluster"><input type="checkbox" checked={allowWaitlist} onChange={(event) => setAllowWaitlist(event.target.checked)} /> Waitlist a party that does not fit</label>
            <div className="field">
              <label htmlFor="reg-note">Confirmation note</label>
              <textarea id="reg-note" value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} />
            </div>
            <div className="cluster">
              <button className="btn primary" type="submit" disabled={saving}>{saving ? "Saving…" : "Create signup"}</button>
              <button className="btn ghost" type="button" onClick={() => setOpen(false)}>Close</button>
            </div>
          </form>
        </Dialog>
      )}
    </PageFrame>
  );
}
